import React from 'react';

import './TodoListTitle.scss';
import { TodoIntegrationType } from '@demind-inc/core';
import { TodoListMoreDropdown, TodoListMoreDropdownOption } from '../TodoListMoreDropdown';
import { useDropdownState } from '../../../../hooks';
import { IconButton } from '@mui/material';
import { MoreVert } from '@mui/icons-material';
import { isAppleRemindersDesktopSupported } from '../../../../helpers/appleRemindersDesktop.helpers';
import { useTranslation } from 'react-i18next';

interface TodoListTitleProps {
  iconSource: string;
  todoType: TodoIntegrationType;
  isDisconnecting?: boolean;
  isSyncing?: boolean;
  onDeleteIntegration: () => void;
  onSyncIntegration: () => void;
}

const TodoListTitle: React.FC<TodoListTitleProps> = ({
  todoType,
  iconSource,
  isDisconnecting = false,
  isSyncing = false,
  onDeleteIntegration,
  onSyncIntegration,
}) => {
  const { t } = useTranslation();
  const { anchorEl, handleCloseDropdown, handleOpenDropdown } = useDropdownState();
  const showMoreMenu = todoType !== 'apple-reminders' || isAppleRemindersDesktopSupported();

  const sourceTitle =
    todoType === 'apple-reminders'
      ? 'Apple Reminders'
      : todoType === 'google-tasks'
        ? 'Google Tasks'
        : todoType === 'clickup'
          ? 'ClickUp'
          : todoType?.charAt(0)?.toUpperCase() + todoType?.slice(1);

  const handleMoreDropdownItemClick = async (option: TodoListMoreDropdownOption) => {
    if (option === 'disconnect') {
      onDeleteIntegration();
    } else if (option === 'sync') {
      onSyncIntegration();
    }
    handleCloseDropdown();
  };

  const renderLoadingOption = () => {
    if (isDisconnecting) {
      return 'disconnect';
    } else if (isSyncing) {
      return 'sync';
    }
    return undefined;
  };

  return (
    <>
      <div className="todo-list-title">
        <img src={iconSource} alt={todoType} className="todo-list-title__icon" />
        <div className="todo-list-title__text">{sourceTitle}</div>
        {showMoreMenu && (
          <IconButton
            aria-label={t('task.todoIntegration.moreActions', { source: sourceTitle })}
            size="small"
            color="inherit"
            className="todo-list-title__more"
            onClick={(e) => {
              e.stopPropagation();
              handleOpenDropdown(e);
            }}
          >
            <MoreVert className="todo-list-title__more__icon" />
          </IconButton>
        )}
      </div>
      {showMoreMenu && (
        <TodoListMoreDropdown
          anchorEl={anchorEl}
          sourceTitle={sourceTitle}
          todoType={todoType}
          onClose={handleCloseDropdown}
          loadingOption={renderLoadingOption()}
          onClickItem={handleMoreDropdownItemClick}
        />
      )}
    </>
  );
};

export default TodoListTitle;
